import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Modal } from 'components/Modal';
import {
  GET_RETURN_INCENTIVE_COLLECTIONS,
  GET_RETURN_INCENTIVE_PRODUCTS,
} from 'gql/incentiveCatalog';
import { IncentiveCatalogReference } from 'types/incentive';

export type IncentiveCatalogPickerProps = {
  storeId: string;
  selected: IncentiveCatalogReference[];
  onChange: (references: IncentiveCatalogReference[]) => void;
  onClose: () => void;
};

export const IncentiveCatalogPicker = ({
  storeId,
  selected,
  onChange,
  onClose,
  resource,
}: IncentiveCatalogPickerProps & { resource: 'products' | 'collections' }) => {
  const [term, setTerm] = useState('');
  const [submittedTerm, setSubmittedTerm] = useState('');
  const [paginationError, setPaginationError] = useState(false);
  const isProducts = resource === 'products';
  const resultKey = isProducts
    ? 'returnIncentiveProducts'
    : 'returnIncentiveCollections';
  const capitalizedResource = isProducts ? 'Products' : 'Collections';
  const [search, { data, loading, error, fetchMore }] = useLazyQuery(
    isProducts
      ? GET_RETURN_INCENTIVE_PRODUCTS
      : GET_RETURN_INCENTIVE_COLLECTIONS,
    { fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true },
  );
  const runSearch = (value: string) => {
    setPaginationError(false);
    setSubmittedTerm(value.trim());
    search({ variables: { input: { storeId, search: value.trim() } } });
  };
  useEffect(() => {
    runSearch('');
    // Fetch the selected store's catalog when this picker opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);
  const catalog = data?.[resultKey];
  const references: IncentiveCatalogReference[] = catalog?.nodes || [];
  const selectedIds = new Set(selected.map(({ id }) => id));

  return (
    <Modal
      open
      openModal={onClose}
      containerClass="w-full max-w-2xl !p-0 overflow-hidden"
    >
      <div className="px-6 pb-6 pt-1">
        <h2 className="mb-5 text-lg font-semibold text-gray-900">
          Select {resource}
        </h2>
        <form
          role="search"
          className="mb-4 flex gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            runSearch(term);
          }}
        >
          <input
            type="search"
            aria-label={`Search ${resource}`}
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={`Search ${resource}…`}
            className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={loading}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm disabled:opacity-50"
          >
            Search
          </button>
        </form>
        {error ? (
          <p
            role="alert"
            className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {capitalizedResource} could not be loaded. Try searching again.
          </p>
        ) : null}
        {paginationError ? (
          <p
            role="alert"
            className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            More {resource} could not be loaded. Try again.
          </p>
        ) : null}
        {loading ? (
          <p className="py-4 text-sm text-gray-500">Loading {resource}…</p>
        ) : null}
        {!loading && !error && references.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            No {resource} found.
          </p>
        ) : null}
        <ul className="max-h-[50vh] space-y-2 overflow-y-auto">
          {references.map((reference) => {
            const isSelected = selectedIds.has(reference.id);
            return (
              <li key={reference.id}>
                <button
                  type="button"
                  aria-label={`${isSelected ? 'Remove' : 'Select'} ${
                    reference.title
                  }`}
                  aria-pressed={isSelected}
                  onClick={() =>
                    onChange(
                      isSelected
                        ? selected.filter(({ id }) => id !== reference.id)
                        : [...selected, reference],
                    )
                  }
                  className={`flex w-full items-center justify-between rounded-md border px-3 py-3 text-left text-sm ${
                    isSelected
                      ? 'border-blue-400 bg-blue-50 text-blue-900'
                      : 'border-gray-200 text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span>{reference.title}</span>
                  <span>{isSelected ? 'Selected' : 'Select'}</span>
                </button>
              </li>
            );
          })}
        </ul>
        {catalog?.pageInfo?.hasNextPage ? (
          <button
            type="button"
            disabled={loading}
            onClick={async () => {
              setPaginationError(false);
              try {
                await fetchMore({
                  variables: {
                    input: {
                      storeId,
                      search: submittedTerm,
                      after: catalog.pageInfo.endCursor,
                    },
                  },
                  updateQuery: (previous: any, { fetchMoreResult }: any) => {
                    if (!fetchMoreResult) return previous;
                    const nodes = new Map<string, IncentiveCatalogReference>();
                    [
                      ...previous[resultKey].nodes,
                      ...fetchMoreResult[resultKey].nodes,
                    ].forEach((node) => nodes.set(node.id, node));
                    return {
                      ...fetchMoreResult,
                      [resultKey]: {
                        ...fetchMoreResult[resultKey],
                        nodes: Array.from(nodes.values()),
                      },
                    };
                  },
                });
              } catch {
                setPaginationError(true);
              }
            }}
            className="mt-3 rounded-md border border-gray-300 px-3 py-2 text-sm disabled:opacity-50"
          >
            Load more {resource}
          </button>
        ) : null}
        <div className="mt-5 flex items-center justify-between border-t border-gray-200 pt-4">
          <span className="text-xs text-gray-500">
            {selected.length}{' '}
            {selected.length === 1 ? resource.slice(0, -1) : resource} selected
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
};
